<script setup>
  import { ref, computed, watch, watchEffect, onUnmounted } from "vue";
  import { useMapStore } from "@/stores/MapStore";
  import { useForecastModelStore } from "@/stores/ForecastModelStore";
  import { useSheetController } from '@/composables/useSheetsController';
  import { useDateHelper } from "@/composables/useDateHelper";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import MapSourceSelectorBase from "@/components/MapSourceSelectorBase.vue";
  import MapModelInfoPopUp from "@/components/MapModelInfoPopUp.vue";
  import MapModelRunSelectorPopUp from "@/components/MapModelRunSelectorPopUp.vue";
  import useMapConfigurationGlobals from "@/composables/useMapConfigurationGlobals";
  import Icon from "./icons/Icon.vue";
  
  const { $FORCAST_MODEL_RUN_SELECTOR } = useMapConfigurationGlobals();

  const mapStore = useMapStore();
  const forecastModelStore = useForecastModelStore();
  const dateHelper = useDateHelper();
  const isDesktop = useMediaQuery('(min-width: 840px)');

  let initialLoad = ref(true);
  let isLoading = ref(true);

  const runDate = computed(() => {
    if (!forecastModelStore.selectedModelRunTime) return;

    return dateHelper.modelRunTimeFormatted({
      timestamp: forecastModelStore.selectedModelRunTime, 
      utcOffset: mapStore.state.geoInfo.local_utc_offset,
      timezoneName: mapStore.state.geoInfo.local_timezone_short
    })
  });

  const modelInfo = computed(() => ({
    id: forecastModelStore.selectedModelId,
    modelName: forecastModelStore.selectedModelName,
    modelRunName: forecastModelStore.selectedModelRunName,
    runDate: runDate.value
  }));

  function handleShowModelSheet() {
    useSheetController.toggleSheet('source', 'forecast-models', true);
  }

  async function getModels() {
    const isCurrent = mapStore.state.fxParam === 'currents';
    isLoading.value = initialLoad.value ? true : false;

    const { selectedModel } = isCurrent ? await forecastModelStore.getCurrentModels() : await forecastModelStore.getForecastModels();

    if (initialLoad.value) {
      mapStore.state.forecastModelId = selectedModel.model_id
    };
    
    isLoading.value = false;
    initialLoad.value = false;
  }

  await getModels();

  watch(mapStore.bounds, () => {
    getModels();
  });

  watchEffect(() => {
    mapStore.state.fxParam && getModels();
  })

  onUnmounted(() => {
    useSheetController.closeSheet('source');
  });

</script>
<template>
  <MapSourceSelectorBase
    @showSheet="handleShowModelSheet"
    :popUp="$FORCAST_MODEL_RUN_SELECTOR ? 'model-run' : 'info'"
  >
    <template #icon>
      <Icon name="circle-nodes" />
    </template>
    <template #label>
      {{ forecastModelStore.selectedModelDisplayName }}
    </template>
    <template #popup>
      <MapModelRunSelectorPopUp v-if="$FORCAST_MODEL_RUN_SELECTOR && isDesktop" :modelInfo="modelInfo" />
      <MapModelInfoPopUp v-else-if="!$FORCAST_MODEL_RUN_SELECTOR" :modelInfo="modelInfo" />
    </template>
  </MapSourceSelectorBase>
</template>